"use client";

import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible";
import { motion } from "motion/react";
import ChevronDownIcon from "@/shared/icons/chevron-down.svg";

function Collapsible({ ...props }: CollapsiblePrimitive.Root.Props) {
  return (
    <CollapsiblePrimitive.Root
      data-slot="collapsible"
      {...props}
      className="space-y-4"
    />
  );
}

function CollapsibleTrigger({ ...props }: CollapsiblePrimitive.Trigger.Props) {
  return (
    <CollapsiblePrimitive.Trigger
      data-slot="collapsible-trigger"
      {...props}
      className="group w-full pl-3 flex items-center justify-between gap-3 text-xl leading-7 lg:text-[24px] lg:leading-8 font-medium border-l border-white text-left cursor-pointer"
    >
      {props.children}
      <div className="flex size-8 shrink-0 bg-[#C4C4C433] group-data-panel-open:bg-white rounded-full pressable">
        <ChevronDownIcon className="group-data-panel-open:text-black size-6 m-auto transition-transform duration-200 group-data-panel-open:rotate-180" />
      </div>
    </CollapsiblePrimitive.Trigger>
  );
}

function CollapsibleContent({ ...props }: CollapsiblePrimitive.Panel.Props) {
  return (
    <CollapsiblePrimitive.Panel
      data-slot="collapsible-content"
      keepMounted
      {...props}
      render={(
        // motion.div types its own on*Animation/onDrag handlers, so the DOM
        // handlers base-ui provides here must be dropped before spreading.
        // `hidden` is dropped too: base-ui only detects CSS transitions/animations
        // to know when to apply it, so with a JS-driven animation it sets
        // `hidden` (display:none) immediately on close instead of waiting —
        // aria-hidden replaces it without killing the exit animation.
        /* eslint-disable @typescript-eslint/no-unused-vars */
        {
          onDrag,
          onDragStart,
          onDragEnd,
          onAnimationStart,
          onAnimationEnd,
          onAnimationIteration,
          hidden,
          ...renderProps
        },
        /* eslint-enable @typescript-eslint/no-unused-vars */
        state
      ) => (
        <motion.div
          {...renderProps}
          aria-hidden={!state.open}
          initial={false}
          animate={
            state.open
              ? { height: "auto", opacity: 1 }
              : { height: 0, opacity: 0 }
          }
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{ ...renderProps.style, overflow: "hidden" }}
        />
      )}
    />
  );
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
